import { onDocumentReady } from '@theme/utilities';

/**
 * The intersection observer instance for scroll animations
 * @type {IntersectionObserver | null}
 */
let scrollAnimationObserver = null;

/**
 * Initializes the scroll animation system using Intersection Observer
 * Applies classes to elements with 'scroll-trigger' class when they enter/leave viewport
 * @param {Element} [rootEl] - The root element to search within (defaults to document)
 * @param {boolean} [isDesignModeEvent] - Whether this is being called from theme editor
 */
function initializeScrollAnimationTrigger(rootEl = document, isDesignModeEvent = false) {
  const scrollTriggers = Array.from(rootEl.getElementsByClassName('scroll-trigger'));
  
  if (scrollTriggers.length === 0) return;

  // Create intersection observer if it doesn't exist
  if (!scrollAnimationObserver) {
    scrollAnimationObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const element = entry.target;
          
          if (entry.isIntersecting) {
            // Element is in view
            element.classList.add('scroll-trigger--in-view');
            element.classList.add('scroll-trigger--active');
          } else {
            // Element is out of view
            element.classList.remove('scroll-trigger--in-view');
            // Keep 'scroll-trigger--active' once it's been seen
          }
        });
      },
      {
        rootMargin: '0px 0px -50px 0px', // Trigger when element is 50px from bottom of viewport
        threshold: 0.1 // Trigger when 10% of element is visible
      }
    );
  }

  // Observe all scroll trigger elements
  scrollTriggers.forEach((element) => {
    scrollAnimationObserver.observe(element);
  });
}

/**
 * Disconnects the scroll animation observer
 */
function disconnectScrollAnimationObserver() {
  if (scrollAnimationObserver) {
    scrollAnimationObserver.disconnect();
    scrollAnimationObserver = null;
  }
}

// Initialize on DOM ready
onDocumentReady(() => {
  initializeScrollAnimationTrigger();
});

// Handle theme editor section loading
if (window.Shopify?.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    initializeScrollAnimationTrigger(event.target, true);
  });
  
  document.addEventListener('shopify:section:reorder', () => {
    initializeScrollAnimationTrigger(document, true);
  });
}

// Export functions for manual use
export { initializeScrollAnimationTrigger, disconnectScrollAnimationObserver };
